import React, { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import OvCompactButton from '../atoms/OvCompactButton';
import OvErrorMessage from '../atoms/OvErrorMessage';
import OvHlsPlayer from '../atoms/OvHlsPlayer';
import OvVideoThumbnail from '../atoms/OvVideoThumbnail';
import { AdminVideo } from '../../../common/model/dto/video/admin-video';
import { isArchived } from '../../../common/utils/video-status-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoPreviewProps {
  video: AdminVideo;
  children?: ReactNode;
}

const OvVideoPreview: FC<OvVideoPreviewProps> = ({
  video,
  children = null,
}) => {
  const { t } = useTranslation();
  const [hasError, setHasError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const reload = () => {
    setHasError(false);
    setAttempt((current) => current + 1);
  };

  const renderMedia = () => {
    if (video.status === 'ready' && video.hls_url) {
      return (
        <>
          <OvHlsPlayer
            key={attempt}
            src={video.hls_url}
            poster={video.poster_url}
            title={video.title}
            onError={() => setHasError(true)}
          />
          {hasError && (
            <Error>
              <OvErrorMessage message={t('videos.player.error')} />
              <OvCompactButton tone="secondary" onClick={reload}>
                {t('videos.player.reload')}
              </OvCompactButton>
            </Error>
          )}
        </>
      );
    }

    return (
      <>
        <OvVideoThumbnail src={video.poster_url} size="large" />
        {isArchived(video) && (
          <Unavailable>{t('videos.player.unavailable')}</Unavailable>
        )}
      </>
    );
  };

  return (
    <Preview aria-label={t('videos.player.region')}>
      {renderMedia()}
      {children}
    </Preview>
  );
};

export default OvVideoPreview;

const Preview = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${Variables.spacing.SMALL};
  min-width: 0;

  > video,
  > img,
  > div:first-child {
    align-self: stretch;
  }
`;

const Error = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${Variables.spacing.SMALL};
`;

const Unavailable = styled.p`
  margin: 0;
  color: ${Colours.OV_CHARCOAL};
`;
